/**
 * Shared UI primitives — Phase 3 foundation.
 *
 * Ported from the v2 mockup `ui.jsx`. Every Phase 3 tab redesign consumes
 * these. Markup matches the mockup; styling reads from CSS variables in
 * /app/frontend/src/pages/LeadBoard.css so themes + density work everywhere.
 *
 * INLINE-FIRST: no Modal here. Modal exists only for hard-stop confirms.
 */
import { useEffect, useRef, useState } from 'react';
import { Check as CheckIcon, ChevronDown, MoreVertical, Search as SearchIcon, X, ArrowUp, ArrowDown } from 'lucide-react';

// =============================================================
//  Avatar — initials in a colored circle
// =============================================================
const AVATAR_COLORS = ['#11a0c2', '#0b2c5c', '#7c5cff', '#f43f5e', '#10b981', '#f59e0b', '#0c7e9b'];
export function Avatar({ name, size = 28, color }) {
  const initials = (name || '?').split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const idx = name ? (name.charCodeAt(0) + (name.charCodeAt(1) || 0)) % AVATAR_COLORS.length : 0;
  const bg = color || AVATAR_COLORS[idx];
  return (
    <span className="lb-avatar" style={{ width: size, height: size, background: bg, fontSize: Math.round(size * 0.42) }}>
      {initials}
    </span>
  );
}

// =============================================================
//  Btn — primary / ghost / soft / danger; sm; iconOnly
// =============================================================
export function Btn({ variant = 'ghost', icon: Icon, sm = false, iconOnly = false, children, testid, ...rest }) {
  const cls = `lb-btn lb-btn-${variant}${sm ? ' lb-btn-sm' : ''}${iconOnly ? ' lb-btn-icon-only' : ''}`;
  return (
    <button className={cls} data-testid={testid} {...rest}>
      {Icon && <Icon size={sm ? 14 : 15} strokeWidth={2.2} />}
      {!iconOnly && children}
    </button>
  );
}

// =============================================================
//  Badge — tone: ok | warn | bad | info | muted
// =============================================================
export function Badge({ tone = 'muted', dot = true, children, testid }) {
  return (
    <span className={`lb-badge lb-badge-${tone}`} data-testid={testid}>
      {dot && <span className={`lb-bdot lb-bdot-${tone}`} />}
      {children}
    </span>
  );
}

const STATUS_TONE = {
  new: 'info', contacted: 'warn', qualified: 'ok', won: 'ok', lost: 'bad',
  pending: 'warn', paid: 'ok', completed: 'ok', cancelled: 'bad', refunded: 'bad',
  active: 'ok', inactive: 'muted', archived: 'muted', verified: 'ok', unverified: 'warn',
};
export function StatusBadge({ status, testid }) {
  const s = String(status || '').toLowerCase();
  const tone = STATUS_TONE[s] || 'muted';
  return <Badge tone={tone} testid={testid}>{status}</Badge>;
}

const SOURCE_COLOR = {
  contact: '#11a0c2', holiday: '#f43f5e', letstalktaxes: '#7c5cff',
  referral: '#10b981', coffee: '#b8742a', ppc: '#f59e0b', ltt_kgob: '#0c7e9b',
  ppc_ads: '#f59e0b', preregister: '#7c5cff',
};
export function SourceChip({ source, label, testid }) {
  const k = String(source || '').toLowerCase();
  const color = SOURCE_COLOR[k] || '#7185a0';
  return (
    <span className="lb-chip" data-testid={testid}>
      <span className="lb-cdot" style={{ background: color }} />
      {label || source}
    </span>
  );
}

// =============================================================
//  Score — letter grade A/B/C/D
// =============================================================
const GRADE_TONE = { A: '#10b981', B: '#11a0c2', C: '#f59e0b', D: '#ef4444' };
export function Score({ grade, testid }) {
  const g = String(grade || '').toUpperCase().charAt(0) || '—';
  return <span className="lb-score" style={{ background: (GRADE_TONE[g] || '#7185a0') + '22', color: GRADE_TONE[g] || '#7185a0' }} data-testid={testid}>{g}</span>;
}

// =============================================================
//  Check — checkbox with custom styling
// =============================================================
export function Check({ on, onClick, testid }) {
  return (
    <button className={`lb-check${on ? ' on' : ''}`} onClick={onClick} data-testid={testid} aria-checked={on} role="checkbox">
      {on && <CheckIcon size={12} strokeWidth={3} />}
    </button>
  );
}

// =============================================================
//  Switch — on/off toggle (Alerts/Availability)
// =============================================================
export function Switch({ on, onClick, testid, label }) {
  return (
    <button onClick={onClick} className={`lb-switch${on ? ' on' : ''}`} data-testid={testid} role="switch" aria-checked={on} aria-label={label}>
      <span className="lb-switch-knob" />
    </button>
  );
}

// =============================================================
//  StatCard — icon + value + label + optional delta
// =============================================================
export function StatCard({ icon: Icon, value, label, delta, deltaDir, testid }) {
  return (
    <div className="lb-stat" data-testid={testid}>
      <div className="lb-stat-ico">{Icon && <Icon size={18} />}</div>
      <div className="lb-stat-val">{value}</div>
      <div className="lb-stat-label">{label}</div>
      {delta != null && (
        <span className={`lb-stat-delta lb-stat-delta-${deltaDir || 'flat'}`}>
          {deltaDir === 'up' && <ArrowUp size={11} />}
          {deltaDir === 'down' && <ArrowDown size={11} />}
          {delta}
        </span>
      )}
    </div>
  );
}

// =============================================================
//  PageHead — title + subtitle + badge + right actions
// =============================================================
export function PageHead({ title, sub, badge, actions, testid }) {
  return (
    <div className="lb-page-head" data-testid={testid}>
      <div>
        <h1 className="lb-page-title">
          {title}
          {badge && <Badge tone={badge.tone || 'info'}>{badge.text}</Badge>}
        </h1>
        {sub && <p className="lb-page-sub">{sub}</p>}
      </div>
      {actions && <div className="lb-page-actions">{actions}</div>}
    </div>
  );
}

// =============================================================
//  SecHead — section/card header (icon + title + right slot)
// =============================================================
export function SecHead({ title, icon: Icon, right }) {
  return (
    <div className="lb-sec-head">
      <h3 className="lb-sec-title">
        {Icon && <span className="lb-si"><Icon size={15} /></span>}
        {title}
      </h3>
      {right && <div>{right}</div>}
    </div>
  );
}

// =============================================================
//  EmptyState — placeholder when a list is empty
// =============================================================
export function EmptyState({ icon: Icon, title, body, action, testid }) {
  return (
    <div className="lb-empty" data-testid={testid}>
      {Icon && <div className="lb-empty-ico"><Icon size={22} /></div>}
      <h3>{title}</h3>
      {body && <p>{body}</p>}
      {action}
    </div>
  );
}

// =============================================================
//  RowActions — kebab menu on table rows
// =============================================================
export function RowActions({ items, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (ev) => {
      if (ref.current && !ref.current.contains(ev.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <span className="lb-rowact-wrap" ref={ref}>
      <button className="lb-btn lb-btn-ghost lb-btn-sm lb-btn-icon-only" onClick={(ev) => { ev.stopPropagation(); setOpen((o) => !o); }} data-testid={testid}>
        <MoreVertical size={14} />
      </button>
      {open && (
        <div className="lb-rowacts">
          {(items || []).map((it, i) => (
            <button
              key={i}
              className={`lb-rowact${it.danger ? ' danger' : ''}`}
              onClick={(ev) => { ev.stopPropagation(); setOpen(false); it.onClick(); }}
              data-testid={it.testid}
            >
              {it.icon && <it.icon size={14} />}
              {it.title}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

// =============================================================
//  Popover — anchored, dismiss-on-outside-click (no modal)
// =============================================================
export function Popover({ open, onClose, anchorRef, children, align = 'right' }) {
  const popRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (ev) => {
      if (popRef.current?.contains(ev.target)) return;
      if (anchorRef?.current?.contains(ev.target)) return;
      onClose();
    };
    const onEsc = (ev) => { if (ev.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [open, onClose, anchorRef]);
  if (!open) return null;
  return (
    <div ref={popRef} className={`lb-popover lb-popover-${align}`}>
      {children}
    </div>
  );
}

// =============================================================
//  ListToolbar — search + status filter + source filter + date range
//  All filters are optional. Pass null to hide.
// =============================================================
export function ListToolbar({
  search, setSearch,
  status, setStatus, statusOptions,
  source, setSource, sourceOptions,
  range, setRange,
  extraRight,
}) {
  return (
    <div className="lb-toolbar">
      <div className="lb-field lb-field-search">
        <SearchIcon size={15} style={{ color: 'var(--muted)' }} />
        <input
          value={search}
          onChange={(ev) => setSearch(ev.target.value)}
          placeholder="Search by name, email…"
          data-testid="list-search"
        />
        {search && (
          <button className="lb-field-clear" onClick={() => setSearch('')} data-testid="list-search-clear">
            <X size={13} />
          </button>
        )}
      </div>
      {statusOptions && (
        <select
          value={status || ''}
          onChange={(ev) => setStatus(ev.target.value || null)}
          className="lb-field-select"
          data-testid="filter-status"
        >
          <option value="">All statuses</option>
          {(statusOptions || []).map((s) => <option key={s.value || s} value={s.value || s}>{s.label || s}</option>)}
        </select>
      )}
      {sourceOptions && (
        <select
          value={source || ''}
          onChange={(ev) => setSource(ev.target.value || null)}
          className="lb-field-select"
          data-testid="filter-source"
        >
          <option value="">All sources</option>
          {(sourceOptions || []).map((s) => <option key={s.value || s} value={s.value || s}>{s.label || s}</option>)}
        </select>
      )}
      {setRange && (
        <select
          value={range || ''}
          onChange={(ev) => setRange(ev.target.value || null)}
          className="lb-field-select"
          data-testid="filter-range"
        >
          <option value="">All time</option>
          <option value="1d">Today</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
        </select>
      )}
      {extraRight && <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>{extraRight}</div>}
    </div>
  );
}

// =============================================================
//  Pager — page-1/2 chevrons + page size
// =============================================================
export function Pager({ page, setPage, pageSize, setPageSize, total, testid }) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="lb-pager" data-testid={testid}>
      <span>Showing <b>{from}</b>–<b>{to}</b> of <b>{total.toLocaleString()}</b></span>
      <div className="lb-pages">
        <select value={pageSize} onChange={(ev) => { setPageSize(Number(ev.target.value)); setPage(1); }} className="lb-field-select-sm" data-testid="pager-size">
          <option value="20">20</option>
          <option value="25">25</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
        <Btn data-testid="primitives-btn-3" iconOnly icon={ArrowUp} onClick={() => setPage(Math.max(1, page - 1))} testid="pager-prev" sm />
        <span style={{ minWidth: 60, textAlign: 'center', fontVariantNumeric: 'tabular-nums', fontSize: 12.5, fontWeight: 700 }}>
          {page} / {pageCount}
        </span>
        <Btn data-testid="primitives-btn-2" iconOnly icon={ArrowDown} onClick={() => setPage(Math.min(pageCount, page + 1))} testid="pager-next" sm />
      </div>
    </div>
  );
}

// =============================================================
//  TableSkeleton — shimmer placeholder
// =============================================================
export function TableSkeleton({ rows = 6, cols = 5 }) {
  return (
    <div data-testid="table-skeleton">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="lb-tr-skel">
          {Array.from({ length: cols }).map((__, c) => (
            <div key={c} className="lb-skel" style={{ width: `${30 + (c * 13) % 60}%` }} />
          ))}
        </div>
      ))}
    </div>
  );
}

// =============================================================
//  Modal — hard-stop confirm only (use SPARINGLY)
// =============================================================
export function Modal({ open, title, onClose, children, foot, testid }) {
  useEffect(() => {
    if (!open) return;
    const onEsc = (ev) => { if (ev.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="lb-scrim" onMouseDown={onClose} data-testid={testid}>
      <div data-testid="primitives-div-1" className="lb-modal" onMouseDown={(ev) => ev.stopPropagation()}>
        <div className="lb-modal-head">
          <h3>{title}</h3>
          <button className="lb-btn lb-btn-ghost lb-btn-sm lb-btn-icon-only" onClick={onClose} data-testid="modal-close">
            <X size={16} />
          </button>
        </div>
        <div className="lb-modal-body">{children}</div>
        {foot && <div className="lb-modal-foot">{foot}</div>}
      </div>
    </div>
  );
}

// =============================================================
//  Field — labeled input wrapper
// =============================================================
export function Field({ label, children, hint, error }) {
  return (
    <label className="lb-field-row">
      {label && <span className="lb-lbl">{label}</span>}
      {children}
      {error ? <span className="lb-field-err">{error}</span> : hint ? <span className="lb-field-hint">{hint}</span> : null}
    </label>
  );
}

export const ChevronD = ChevronDown;

// =============================================================
//  TabError — error retry UI for any tab. Use after a failed fetch.
// =============================================================
export function TabError({ message, onRetry, testid }) {
  return (
    <div className="lb-empty" data-testid={testid || 'tab-error'} style={{ padding: 28 }}>
      <div className="lb-empty-ico" style={{ background: 'var(--bad-soft)', color: 'var(--bad)' }}>
        <X size={22} />
      </div>
      <h3>Couldn&apos;t load this tab</h3>
      <p>{message || 'Something went wrong while fetching data. Please retry.'}</p>
      {onRetry && (
        <Btn data-testid="primitives-btn-1" variant="primary" sm onClick={onRetry} testid="tab-error-retry">Retry</Btn>
      )}
    </div>
  );
}

// =============================================================
//  SortHeader — th cell with sort indicator (Phase 4 polish)
// =============================================================
export function SortHeader({ field, label, sortField, sortDir, onSort, className }) {
  const active = sortField === field;
  return (
    <th
      className={`sortable${active ? ' sort-key' : ''}${className ? ' ' + className : ''}`}
      onClick={() => onSort(field)}
      data-testid={`sort-${field}`}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        {active && (sortDir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
      </span>
    </th>
  );
}
